import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";
import {
  Sun, Building2, Users, Trash2, ArrowRight, X, Check,
  LogOut, RefreshCw, Pencil, Calendar, StickyNote,
  AlertTriangle, Search, Sprout, Zap, Crown, Briefcase
} from "lucide-react";
import { toast } from "sonner";

const SA_TOKEN_KEY = "sf_sa_token";
const AMBER = "#E0A82E";

const saApi = axios.create({ baseURL: `${BACKEND_URL}/api` });
saApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(SA_TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const PLANS = ["free", "starter", "pro", "business"];

// Palette coerente con DESIGN.md: ambra = accento primario, sky + emerald = secondari
const PLAN_STYLE = {
  free:     { bg: "bg-zinc-50",     text: "text-zinc-600",    border: "border-zinc-200/70",   dot: "bg-zinc-400",    icon: Sprout,    iconColor: "text-zinc-500"    },
  starter:  { bg: "bg-sky-50",      text: "text-sky-700",     border: "border-sky-200/70",    dot: "bg-sky-500",     icon: Zap,       iconColor: "text-sky-600"     },
  pro:      { bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-200/80",  dot: "bg-amber-500",   icon: Crown,     iconColor: "text-amber-600"   },
  business: { bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200/70",dot: "bg-emerald-500", icon: Briefcase, iconColor: "text-emerald-600" },
};

const Eyebrow = ({ children, className = "" }) => (
  <div className={`font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 ${className}`}>{children}</div>
);

function Logo({ label = "Superadmin" }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-zinc-900 flex items-center justify-center text-white">
        <Sun size={17} strokeWidth={2.5}/>
      </div>
      <div className="leading-tight">
        <div className="font-semibold tracking-tight text-zinc-900">SolarFlow</div>
        <Eyebrow className="!tracking-[0.18em] mt-0.5">{label}</Eyebrow>
      </div>
    </div>
  );
}

function PlanBadge({ plan, onClick }) {
  const p = plan || "free";
  const s = PLAN_STYLE[p] || PLAN_STYLE.free;
  const Icon = s.icon;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border font-mono font-semibold uppercase tracking-[0.1em] text-[11px] px-3 py-1 transition-all hover:shadow-sm ${s.bg} ${s.text} ${s.border} ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <Icon size={11} className={s.iconColor}/>
      {p}
      {onClick && <Pencil size={9} className="opacity-50"/>}
    </button>
  );
}

const AVATAR_SIZE = { 9: "w-9 h-9", 10: "w-10 h-10" };

function Avatar({ name, size = 9 }) {
  const initials = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className={`${AVATAR_SIZE[size]} rounded-full bg-zinc-900 text-white flex items-center justify-center font-semibold text-sm shrink-0`}>
      {initials}
    </div>
  );
}

// ─── Login ──────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await saApi.post("/superadmin/login", { secret });
      localStorage.setItem(SA_TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch {
      toast.error("Chiave non valida");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f3f0] text-zinc-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><Logo/></div>
        <div className="bg-white border border-zinc-200/70 rounded-3xl p-7 shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">Pannello di controllo</h1>
          <p className="text-sm text-zinc-500 mt-1 mb-6">Accesso riservato all'amministrazione.</p>
          <form onSubmit={login} className="space-y-3">
            <input
              type="password"
              placeholder="Chiave di accesso…"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !secret}
              className="w-full bg-zinc-900 text-white font-semibold rounded-full px-5 py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-zinc-800 transition-colors"
            >
              {loading ? "Accesso in corso…" : <>Accedi <ArrowRight size={15}/></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Modal piano ──────────────────────────────────────────────────────────────
function PlanModal({ company, onClose, onSaved, onSessionExpired }) {
  const [plan, setPlan] = useState(company.plan || "free");
  const [expiresAt, setExpiresAt] = useState(company.plan_expires_at ? company.plan_expires_at.slice(0, 10) : "");
  const [note, setNote] = useState(company.plan_note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!company?.id) { setError("ID azienda mancante — ricarica la pagina"); return; }
    setError("");
    setSaving(true);
    try {
      await saApi.patch(`/superadmin/companies/${company.id}/plan`, {
        plan,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        note,
      });
      toast.success("Piano aggiornato");
      onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Errore di rete";
      if (e?.response?.status === 403) onSessionExpired();
      else setError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200/70 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
          <div>
            <Eyebrow>Modifica abbonamento</Eyebrow>
            <div className="font-semibold text-zinc-900 mt-1">{company.name || company.id?.slice(0, 12) + "…"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors">
            <X size={16}/>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Piano selector */}
          <div>
            <Eyebrow className="mb-2.5">Piano</Eyebrow>
            <div className="grid grid-cols-2 gap-2.5">
              {PLANS.map((p) => {
                const s = PLAN_STYLE[p];
                const active = plan === p;
                const Icon = s.icon;
                return (
                  <button
                    key={p}
                    onClick={() => setPlan(p)}
                    className={`flex items-center gap-2.5 px-4 py-3.5 rounded-2xl border text-sm font-semibold transition-all ${
                      active
                        ? "bg-zinc-900 text-white border-zinc-900 shadow-md"
                        : "bg-white border-zinc-200/70 text-zinc-500 hover:border-zinc-300"
                    }`}
                  >
                    <Icon size={15} className={active ? "text-amber-400" : s.iconColor}/>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                    {active && <Check size={13} className="ml-auto text-amber-400"/>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scadenza */}
          <div>
            <Eyebrow className="flex items-center gap-1.5 mb-2"><Calendar size={10}/> Scadenza (opzionale)</Eyebrow>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            {expiresAt && (
              <div className="text-xs text-zinc-400 mt-1.5">
                Scade il {new Date(expiresAt).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
              </div>
            )}
          </div>

          {/* Nota */}
          <div>
            <Eyebrow className="flex items-center gap-1.5 mb-2"><StickyNote size={10}/> Nota interna (opzionale)</Eyebrow>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="es. Pagato con bonifico il 30/06/2026"
              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-1 flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 rounded-xl px-4 py-3 text-sm">
            <AlertTriangle size={14} className="shrink-0"/>
            {error}
          </div>
        )}

        <div className="px-6 py-4 border-t border-zinc-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors">
            Annulla
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-zinc-800 transition-colors"
          >
            <Check size={14}/> {saving ? "Salvo…" : "Salva piano"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal dettaglio ──────────────────────────────────────────────────────────
function CompanyDetailModal({ companyId, onClose, onDeleted, onEditPlan }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    saApi.get(`/superadmin/companies/${companyId}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [companyId]);

  const deleteCompany = async () => {
    setDeleting(true);
    try {
      await saApi.delete(`/superadmin/companies/${companyId}`);
      toast.success("Azienda eliminata definitivamente");
      onDeleted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setDeleting(false);
    }
  };

  const displayName = data?.name || data?.id?.slice(0, 12) + "…";

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-zinc-200/70 max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-zinc-100">
          {data && <Avatar name={data.name} size={10}/>}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-zinc-900 truncate">{loading ? "Caricamento…" : displayName}</div>
            {data && <div className="text-xs text-zinc-400 font-mono mt-0.5 truncate">{data.id}</div>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 shrink-0 transition-colors">
            <X size={16}/>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {loading && <div className="flex items-center justify-center py-8 text-zinc-400 text-sm">Caricamento…</div>}
          {data && (
            <>
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Email", value: data.email },
                  { label: "P.IVA", value: data.vat_number },
                  { label: "Telefono", value: data.phone },
                  { label: "Indirizzo", value: data.address },
                  { label: "Creata", value: data.created_at ? new Date(data.created_at).toLocaleDateString("it-IT") : null },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <Eyebrow className="mb-1">{label}</Eyebrow>
                    <div className="text-sm text-zinc-700">{value || <span className="text-zinc-300">—</span>}</div>
                  </div>
                ))}
                <div>
                  <Eyebrow className="mb-1.5">Piano attivo</Eyebrow>
                  <PlanBadge plan={data.plan} onClick={() => { onClose(); onEditPlan(data); }}/>
                </div>
              </div>

              {/* Piano dettaglio */}
              {(data.plan_expires_at || data.plan_note) && (
                <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-4 space-y-1.5 text-sm">
                  {data.plan_expires_at && (
                    <div className="flex items-center gap-2 text-amber-800">
                      <Calendar size={13}/>
                      Scade il {new Date(data.plan_expires_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  )}
                  {data.plan_note && (
                    <div className="flex items-center gap-2 text-amber-700">
                      <StickyNote size={13}/>
                      {data.plan_note}
                    </div>
                  )}
                </div>
              )}

              {/* Utenti */}
              <div>
                <Eyebrow className="flex items-center gap-2 mb-3"><Users size={10}/> Utenti ({data.users?.length || 0})</Eyebrow>
                <div className="space-y-2">
                  {(data.users || []).map((u) => (
                    <div key={u.id} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200/70 rounded-2xl p-3">
                      <Avatar name={u.name}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">{u.name || "—"}</div>
                        <div className="text-xs text-zinc-400 truncate">{u.email}</div>
                      </div>
                      <span className={`font-mono text-[9px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded ${
                        u.role === "owner" ? "text-amber-700 bg-amber-50" :
                        u.role === "admin" ? "text-sky-700 bg-sky-50" :
                        "text-zinc-500 bg-zinc-100"
                      }`}>{u.role}</span>
                    </div>
                  ))}
                  {!data.users?.length && <div className="text-sm text-zinc-400 text-center py-4">Nessun utente</div>}
                </div>
              </div>

              {/* Zona pericolosa */}
              <div className="border border-rose-100 rounded-2xl p-4 bg-rose-50/40">
                <div className="flex items-center gap-2 text-rose-600 font-semibold text-sm mb-2">
                  <AlertTriangle size={14}/> Zona pericolosa
                </div>
                <p className="text-xs text-rose-500 mb-3 leading-relaxed">
                  Elimina l'azienda e tutti i dati associati (utenti, clienti, preventivi, simulazioni). Irreversibile.
                </p>
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm font-medium hover:bg-rose-50 transition-colors flex items-center gap-2"
                  >
                    <Trash2 size={13}/> Elimina azienda
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={deleteCompany}
                      disabled={deleting}
                      className="px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      <Trash2 size={13}/> {deleting ? "Elimino…" : "Conferma"}
                    </button>
                    <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700">
                      Annulla
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-zinc-100 flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Superadmin() {
  const [token, setToken] = useState(() => localStorage.getItem(SA_TOKEN_KEY));
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState(null);
  const [planModal, setPlanModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get("/superadmin/companies");
      setCompanies(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        localStorage.removeItem(SA_TOKEN_KEY);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) load(); }, [token, load]);

  const logout = () => {
    localStorage.removeItem(SA_TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginScreen onLogin={setToken}/>;

  const filtered = companies.filter((c) => {
    if (planFilter && (c.plan || "free") !== planFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q);
  });

  const planCounts = PLANS.reduce((acc, p) => {
    acc[p] = companies.filter((c) => (c.plan || "free") === p).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#f4f3f0] text-zinc-900">
      {planModal && (
        <PlanModal
          company={planModal}
          onClose={() => setPlanModal(null)}
          onSaved={load}
          onSessionExpired={() => { toast.error("Sessione scaduta — rieffettua il login"); logout(); }}
        />
      )}
      {detailModal && (
        <CompanyDetailModal
          companyId={detailModal}
          onClose={() => setDetailModal(null)}
          onDeleted={() => { load(); setDetailModal(null); }}
          onEditPlan={(c) => setPlanModal(c)}
        />
      )}

      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#f4f3f0]/80 backdrop-blur-md border-b border-zinc-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Logo/>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2.5 rounded-full text-zinc-400 hover:bg-white hover:text-zinc-900 transition-colors" title="Aggiorna">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""}/>
            </button>
            <button onClick={logout} className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-zinc-200 bg-white text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors">
              <LogOut size={13}/> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">

        {/* Intro */}
        <div>
          <Eyebrow>Gestione aziende</Eyebrow>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">
            {companies.length} {companies.length === 1 ? "azienda" : "aziende"} sulla piattaforma.
          </h1>
          <p className="mt-2 text-zinc-500">Filtra per piano, imposta gli abbonamenti manualmente, gestisci gli accessi.</p>
        </div>

        {/* Filtri / stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Tutte */}
          <button
            onClick={() => setPlanFilter(null)}
            className={`rounded-3xl border p-5 text-left transition-all ${
              planFilter === null
                ? "bg-zinc-900 border-zinc-900 text-white shadow-xl shadow-black/10"
                : "bg-white border-zinc-200/70 hover:border-zinc-300"
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${planFilter === null ? "bg-white/10 text-amber-400" : "bg-zinc-900 text-white"}`}>
              <Building2 size={18}/>
            </div>
            <div className={`text-3xl font-semibold tracking-tight tabular-nums ${planFilter === null ? "text-white" : "text-zinc-900"}`}>{companies.length}</div>
            <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mt-1 ${planFilter === null ? "text-white/50" : "text-zinc-400"}`}>Tutte</div>
          </button>

          {/* Per piano */}
          {PLANS.map((p) => {
            const s = PLAN_STYLE[p];
            const active = planFilter === p;
            const Icon = s.icon;
            return (
              <button
                key={p}
                onClick={() => setPlanFilter(active ? null : p)}
                className={`rounded-3xl border p-5 text-left transition-all ${
                  active
                    ? "bg-zinc-900 border-zinc-900 text-white shadow-xl shadow-black/10"
                    : "bg-white border-zinc-200/70 hover:border-zinc-300"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 border ${active ? "bg-white/10 border-white/10" : `${s.bg} ${s.border}`}`}>
                  <Icon size={18} className={active ? "text-amber-400" : s.iconColor}/>
                </div>
                <div className={`text-3xl font-semibold tracking-tight tabular-nums ${active ? "text-white" : "text-zinc-900"}`}>{planCounts[p]}</div>
                <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mt-1 ${active ? "text-white/50" : "text-zinc-400"}`}>{p}</div>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome o email…"
            className="w-full bg-white border border-zinc-200/70 rounded-2xl pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700">
              <X size={15}/>
            </button>
          )}
        </div>

        {/* Lista */}
        <div className="bg-white border border-zinc-200/70 rounded-3xl overflow-hidden">
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
            <Eyebrow>{filtered.length} {filtered.length === 1 ? "risultato" : "risultati"}</Eyebrow>
            {planFilter && (
              <button
                onClick={() => setPlanFilter(null)}
                className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${PLAN_STYLE[planFilter].bg} ${PLAN_STYLE[planFilter].text} ${PLAN_STYLE[planFilter].border}`}
              >
                {planFilter} <X size={9}/>
              </button>
            )}
            {search && <Eyebrow>· "{search}"</Eyebrow>}
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 size={32} className="mx-auto text-zinc-200 mb-3"/>
              <div className="text-sm text-zinc-400">{loading ? "Caricamento…" : search || planFilter ? "Nessuna azienda trovata" : "Nessuna azienda registrata"}</div>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {filtered.map((c) => (
                <div
                  key={c.id}
                  onClick={() => setDetailModal(c.id)}
                  className="flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-zinc-50/70 transition-colors group cursor-pointer"
                >
                  <Avatar name={c.name}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-900 text-sm truncate">
                      {c.name || <span className="text-zinc-300 italic font-normal">senza nome</span>}
                    </div>
                    <div className="text-xs text-zinc-400 truncate mt-0.5">
                      {c.email || <span className="text-zinc-300">nessuna email</span>}
                    </div>
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <PlanBadge plan={c.plan} onClick={() => setPlanModal(c)}/>
                  </div>

                  <div className="hidden md:flex items-center gap-1.5 text-zinc-500 text-sm w-14 justify-center">
                    <Users size={13} className="text-zinc-400"/>
                    <span className="font-medium tabular-nums">{c.users_count}</span>
                  </div>

                  <div className="hidden lg:block w-24 text-center">
                    {c.plan_expires_at ? (
                      <div className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200/80 rounded-lg px-2 py-1 tabular-nums">
                        {new Date(c.plan_expires_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                      </div>
                    ) : (
                      <span className="text-zinc-300 text-xs">—</span>
                    )}
                  </div>

                  <ArrowRight size={16} className="text-zinc-300 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all shrink-0"/>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center text-xs text-zinc-400 pt-2">
          SolarFlow Pro · Pannello superadmin
        </div>
      </div>
    </div>
  );
}
